/**
 * WhatsAppOptInModal — Collect user's WhatsApp number for manual broadcast
 * Shows after 3rd dashboard visit, dismissible, stores preference in localStorage
 */

import { useState } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { useAuth } from '../../hooks/firebase/useAuth';
import { buildApiUrl } from '../../config/api.config';
import { authenticatedFetch } from '../../utils/authenticatedFetch';

const STORAGE_KEY = 'wa_optin_dismissed';
const VISIT_COUNT_KEY = 'dashboard_visit_count';
const MIN_VISITS = 3;

export function shouldShowWhatsAppModal(): boolean {
  if (localStorage.getItem(STORAGE_KEY) === 'true') return false;
  const visits = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
  return visits >= MIN_VISITS;
}

export function incrementDashboardVisit(): void {
  const visits = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
  localStorage.setItem(VISIT_COUNT_KEY, String(visits + 1));
}

interface WhatsAppOptInModalProps {
  isOpen: boolean;
  onClose: () => void;
  dismissPermanently?: boolean;
}

export function WhatsAppOptInModal({ isOpen, onClose, dismissPermanently = true }: WhatsAppOptInModalProps) {
  const { user } = useAuth();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleDismiss = () => {
    if (dismissPermanently) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (!user?.uid || !phone.trim()) return;

    setError('');
    setSaving(true);

    try {
      const response = await authenticatedFetch(
        buildApiUrl(`/api/users/${user.uid}/phone`),
        {
          method: 'PATCH',
          body: JSON.stringify({ phone: phone.trim() }),
        }
      );

      const result = await response.json() as { success: boolean; error?: string };
      if (result.success) {
        setSuccess(true);
        localStorage.setItem(STORAGE_KEY, 'true');
        setTimeout(onClose, 1500);
      } else {
        setError(result.error || 'Gagal menyimpan nomor.');
      }
    } catch {
      setError('Terjadi kesalahan. Coba lagi nanti.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Tutup"
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-green-100 mb-4">
          <MessageCircle className="text-green-600" size={24} />
        </div>

        {success ? (
          <div>
            <h3 className="font-bold text-lg text-slate-800">Berhasil!</h3>
            <p className="text-sm text-slate-500 mt-1">
              Nomor WhatsApp Anda telah disimpan. Kami akan mengirim sinyal harian via WA.
            </p>
          </div>
        ) : (
          <>
            <h3 className="font-bold text-lg text-slate-800">
              Ingin sinyal harian via WhatsApp?
            </h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Dapatkan notifikasi sinyal langsung ke WhatsApp Anda setiap hari. Gratis, tanpa spam.
            </p>

            {/* Phone input */}
            <div className="mb-3">
              <div className="flex items-center border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-[#00bcd4] focus-within:border-transparent overflow-hidden">
                <span className="text-sm text-slate-500 pl-3 pr-1 select-none">+62</span>
                <input
                  type="tel"
                  placeholder="812 3456 7890"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d\s\-]/g, ''))}
                  className="flex-1 px-2 py-2.5 text-sm outline-none"
                  maxLength={15}
                />
              </div>
              {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            </div>

            <button
              onClick={handleSubmit}
              disabled={!phone.trim() || saving}
              className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan...' : 'Simpan Nomor'}
            </button>

            <button
              onClick={handleDismiss}
              className="w-full mt-2 py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Nanti saja
            </button>
          </>
        )}
      </div>
    </div>
  );
}
